import { EmployeeStatsComparisonScope, type Prisma } from "@prisma/client";
import { type AsyncReturnType } from "type-fest";
import { type AppContext } from "~/lib/context";
import { assertNotNil, hasNonNullableField } from "~/lib/utils";
import {
  aggregateMarketDataResults,
  remapComptryxMarketDataPercentiles,
  selectComptryxMarketDataPercentilesAmounts,
} from "~/services/comptryx/fetchComptryxMarketData";
import { type selectForComptryxMarketDataColumns } from "~/services/external-employee/fetchExternalEmployeesForPeopleDashboard";

type BaseExternalEmployee = Prisma.ExternalEmployeeGetPayload<{
  select: typeof selectForComptryxMarketDataColumns;
}>;

export const enrichExternalEmployeesWithComptryxMarketData = async <T extends BaseExternalEmployee>(
  ctx: AppContext,
  params: {
    isComptryxMarketDataColumnsVisible: boolean;
    externalEmployees: T[];
  }
) => {
  if (!params.isComptryxMarketDataColumnsVisible) {
    return params.externalEmployees.map((externalEmployee) => ({
      ...externalEmployee,
      comptryxMarketDataResult: undefined,
    }));
  }

  const comptryxMarketDataResults = await fetchMarketDataResultsForExternalEmployees(ctx, {
    externalEmployees: params.externalEmployees,
  });

  return params.externalEmployees.map((externalEmployee) => {
    const comptryxMarketDataResult = enrichExternalEmployeeWithComptryxMarketData({
      comptryxMarketDataResults,
      externalEmployee,
    });

    return {
      ...externalEmployee,
      comptryxMarketDataResult,
    };
  });
};

const fetchMarketDataResultsForExternalEmployees = async (
  ctx: AppContext,
  params: {
    externalEmployees: BaseExternalEmployee[];
  }
) => {
  const benchmarkableEmployees = params.externalEmployees.filter(
    (employee) =>
      hasNonNullableField(employee, "comptryxJobId") &&
      hasNonNullableField(employee, "comptryxLevelId") &&
      hasNonNullableField(employee, "comptryxLocation")
  );

  return ctx.prisma.comptryxMarketDataResult.findMany({
    where: {
      OR: benchmarkableEmployees.map((employee) => ({
        jobId: employee.comptryxJobId,
        levelId: employee.comptryxLevelId,
        location: {
          countryId: employee.comptryxLocation.countryId,
        },
        filterId: null,
      })),
    },
    select: {
      jobId: true,
      levelId: true,
      locationId: true,
      location: {
        select: {
          id: true,
          country: { select: { id: true, name: true } },
        },
      },
      baseSalary: { select: selectComptryxMarketDataPercentilesAmounts },
      onTargetTotalCash: { select: selectComptryxMarketDataPercentilesAmounts },
      actualTotalCash: { select: selectComptryxMarketDataPercentilesAmounts },
      currency: true,
      employeesCount: true,
      companiesCount: true,
    },
  });
};

type MarketDataResult = AsyncReturnType<typeof fetchMarketDataResultsForExternalEmployees>[number];

const enrichExternalEmployeeWithComptryxMarketData = (params: {
  comptryxMarketDataResults: MarketDataResult[];
  externalEmployee: BaseExternalEmployee;
}) => {
  const exactComptryxMarketDataResult = params.comptryxMarketDataResults.find((result) =>
    isExactMatch(result, params.externalEmployee)
  );
  if (exactComptryxMarketDataResult) {
    const measures = processMarketDataResults({
      results: [exactComptryxMarketDataResult],
      externalEmployee: params.externalEmployee,
    });

    return {
      measures,
      comparisonScope: EmployeeStatsComparisonScope.LOCATION,
    };
  }

  const countryComptryxMarketDataResults = params.comptryxMarketDataResults.filter((result) =>
    isCountryMatch(result, params.externalEmployee)
  );
  if (countryComptryxMarketDataResults.length > 0) {
    const comparedCountry = assertNotNil(countryComptryxMarketDataResults[0]).location.country;

    const measures = processMarketDataResults({
      results: countryComptryxMarketDataResults,
      externalEmployee: params.externalEmployee,
    });

    return {
      measures,
      comparisonScope: EmployeeStatsComparisonScope.COUNTRY,
      comparedCountry: comparedCountry,
    };
  }
};

const isExactMatch = (result: MarketDataResult, externalEmployee: BaseExternalEmployee) => {
  return (
    result.jobId === externalEmployee.comptryxJobId &&
    result.levelId === externalEmployee.comptryxLevelId &&
    result.location.id === externalEmployee.comptryxLocation?.id
  );
};

const isCountryMatch = (result: MarketDataResult, externalEmployee: BaseExternalEmployee) => {
  return (
    result.jobId === externalEmployee.comptryxJobId &&
    result.levelId === externalEmployee.comptryxLevelId &&
    result.location.country.id === externalEmployee.comptryxLocation?.countryId
  );
};

const processMarketDataResults = (params: { results: MarketDataResult[]; externalEmployee: BaseExternalEmployee }) => {
  const results = params.results.map((result) => ({
    ...result,
    baseSalary: remapComptryxMarketDataPercentiles({
      percentiles: result.baseSalary,
      currency: result.currency,
      targetCurrency: params.externalEmployee.currency,
    }),
    onTargetTotalCash: remapComptryxMarketDataPercentiles({
      percentiles: result.onTargetTotalCash,
      currency: result.currency,
      targetCurrency: params.externalEmployee.currency,
    }),
    actualTotalCash: remapComptryxMarketDataPercentiles({
      percentiles: result.actualTotalCash,
      currency: result.currency,
      targetCurrency: params.externalEmployee.currency,
    }),
  }));

  return aggregateMarketDataResults({ results, targetCurrency: params.externalEmployee.currency });
};
